import { useState, useEffect, createContext, useContext, useCallback, useRef } from "react";
import axios from "axios";
import { API_URL } from "@/utils/apiConfig";
import { initErrorLogger, resetErrorLogger, captureError } from "@/services/errorLogger";
import { changeLanguage } from "@/i18n";

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api/v1`;

// Global axios interceptor: unwrap v1 response envelope transparently
axios.interceptors.response.use(
  (response) => {
    const d = response.data;
    if (d && typeof d === "object" && d.success === true && "data" in d) {
      response.data = d.data;
    }
    return response;
  },
  (error) => {
    const d = error.response?.data;
    if (d && d.success === false && d.error) {
      error.response.data.detail = d.error.message;
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

const HIDDEN_SIDEBAR = {
  gerente: ["intelligence", "settings", "financiero", "reports", "leads", "subscription"],
  sirviente: [
    "intelligence",        // IA / Análise
    "settings",            // Configurações
    "financiero",          // Financeiro
    "marketing",           // Marketing
    "customers",           // Clientes
    "reservations",        // Reservas
    "reports",             // Relatórios
    "leads",               // Leads
    "subscription",        // Assinatura
    "team",                // Equipe
    "stock",               // Inventário
    "kitchen-analytics",   // Análises de cozinha
    "abandoned-orders",    // Pedidos abandonados
    "marketplace",         // Marketplace
    "integrations",        // Integrações
    "personalization",     // Personalização
    "brain",               // Kaiso Brain
    "ai-insights",         // IA Executiva
    "ai-assistant",        // IA Assistente
    "antifurto",           // Antifurto IA
    "menu-engineering",    // Engenharia de Menu
    "smart-menu",          // Menu Inteligente
    "combos",              // Combos
    "franchise",           // Franquias
    "relatorios",          // Relatórios (pt)
    "reportes",            // Relatórios (es)
  ],
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const isRefreshing = useRef(false);
  const refreshQueue = useRef([]);

  const saveTokens = useCallback((accessToken, refreshToken) => {
    localStorage.setItem("token", accessToken);
    if (refreshToken) localStorage.setItem("refresh_token", refreshToken);
    setToken(accessToken);
  }, []);

  const clearTokens = useCallback(() => {
    console.warn("[CLEAR_TOKENS_CALLED]", new Error().stack?.split("\n")[2]?.trim() || "unknown caller");
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    setToken(null);
    setUser(null);
  }, []);

  const logout = useCallback(async () => {
    try {
      const t = localStorage.getItem("token");
      if (t) {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${t}` }
        }).catch(() => {});
      }
    } finally {
      clearTokens();
      resetErrorLogger();
    }
  }, [clearTokens]);

  // Silent refresh: exchange refresh token for new access+refresh pair
  const silentRefresh = useCallback(async () => {
    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken || isRefreshing.current) {
      // If no refresh token and we have admin original tokens, restore admin session
      const adminToken = localStorage.getItem("admin_original_token");
      const adminRefresh = localStorage.getItem("admin_original_refresh");
      if (adminToken && adminRefresh) {
        localStorage.setItem("token", adminToken);
        localStorage.setItem("refresh_token", adminRefresh);
        localStorage.removeItem("admin_original_token");
        localStorage.removeItem("admin_original_refresh");
        setToken(adminToken);
        return adminToken;
      }
      return null;
    }

    isRefreshing.current = true;
    try {
      const res = await axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken });
      const { access_token, refresh_token: newRefresh, user: refreshedUser } = res.data;
      saveTokens(access_token, newRefresh);
      // Update user data if returned by refresh endpoint (keeps restaurant_id in sync)
      if (refreshedUser) {
        setUser(refreshedUser);
        if (refreshedUser.language) changeLanguage(refreshedUser.language);
      }
      isRefreshing.current = false;
      // Process queued requests
      refreshQueue.current.forEach(cb => cb(access_token));
      refreshQueue.current = [];
      return access_token;
    } catch (err) {
      isRefreshing.current = false;
      refreshQueue.current = [];
      // Only clear tokens on definitive auth failures, not transient errors
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        clearTokens();
        window.location.href = "/login";
      }
      return null;
    }
  }, [saveTokens, clearTokens]);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        // Pre-check JWT expiry to avoid a visible 401 on page reload
        let activeToken = token;
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.exp * 1000 <= Date.now() + 5000) {
            // Token already expired (or expiring in 5s) — refresh before calling /auth/me
            const refreshed = await silentRefresh();
            if (!refreshed) { setLoading(false); return; }
            activeToken = refreshed;
          }
        } catch { /* malformed token — fall through and let /auth/me 401 handle it */ }

        try {
          const res = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${activeToken}` }
          });
          setUser(res.data);
          if (res.data?.language) changeLanguage(res.data.language);
          try {
            const payload = JSON.parse(atob(activeToken.split('.')[1]));
            setIsImpersonating(!!payload.impersonated_by);
          } catch { setIsImpersonating(false); }
        } catch (e) {
          // If 401, try silent refresh before giving up
          if (e.response?.status === 401) {
            const newToken = await silentRefresh();
            if (newToken) {
              try {
                const res = await axios.get(`${API}/auth/me`, {
                  headers: { Authorization: `Bearer ${newToken}` }
                });
                setUser(res.data);
                if (res.data?.language) changeLanguage(res.data.language);
              } catch {
                clearTokens();
              }
            }
          } else if (e.response?.status === 403) {
            // Definitive auth failure (blocked account, etc.)
            clearTokens();
          }
          // For network errors (no response), keep existing state - don't log out
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token, silentRefresh, clearTokens]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const data = res.data;
    if (!data || typeof data !== "object") throw new Error(`Login: resposta inesperada do servidor (${typeof data})`);
    if (data.requires_2fa) return data;
    saveTokens(data.access_token, data.refresh_token);
    setUser(data.user);
    if (data.user?.language) changeLanguage(data.user.language);
    return data;
  };

  const login2fa = async (userId, code) => {
    const res = await axios.post(`${API}/auth/login-2fa`, { user_id: userId, code });
    saveTokens(res.data.access_token, res.data.refresh_token);
    setUser(res.data.user);
    if (res.data.user?.language) changeLanguage(res.data.user.language);
    return res.data;
  };

  const register = async (email, password, name, restaurant_name, referral_code = "", country = "") => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name, restaurant_name, referral_code, country });
    saveTokens(res.data.access_token, res.data.refresh_token);
    setUser(res.data.user);
    if (res.data.user?.language) changeLanguage(res.data.user.language);
    return res.data;
  };

  const role = user?.role || "owner";
  const permissions = user?.permissions || {};
  const isOwner = role === "owner" || role === "gerente_manager";
  const isGerente = role === "gerente";
  const isSirviente = role === "sirviente";
  const canSeeFinancials = isOwner || permissions.view_financial_reports || permissions.view_order_totals;
  const isSidebarHidden = (pathKey) => {
    if (isOwner) return false;
    return (HIDDEN_SIDEBAR[role] || []).includes(pathKey);
  };

  // ─── Cross-tab logout detection ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "token") {
        if (!e.newValue) console.warn("[CROSS_TAB_TOKEN_REMOVED]", { had_token: !!e.oldValue });
        else console.log("[CROSS_TAB_TOKEN_SET]", { token_len: e.newValue.length, first8: e.newValue.slice(0, 8) });
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // ─── Proactive token refresh: renew at 80% of TTL, never wait for 401 ───────────────────────
  useEffect(() => {
    if (!token) return;
    let timer;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000;
      const now = Date.now();
      const ttl = exp - now;
      if (ttl <= 0) { silentRefresh(); return; }
      const refreshIn = ttl * 0.8; // refresh at 80% of TTL
      timer = setTimeout(() => silentRefresh(), refreshIn);
    } catch {}
    return () => clearTimeout(timer);
  }, [token, silentRefresh]);

  // authAxios — global timeout 30s + silent refresh on 401 + retry on 5xx/network
  const authAxios = axios.create({ baseURL: API, timeout: 30000 });
  // Inject into error logger so it can ship errors to the backend
  initErrorLogger(authAxios);
  authAxios.interceptors.request.use(config => {
    const t = localStorage.getItem("token");
    if (t) {
      config.headers.Authorization = `Bearer ${t}`;
      try {
        const p = JSON.parse(atob(t.split(".")[1]));
        config._diagRid = p.restaurant_id; // attached for 403 response handler
      } catch {}
    } else {
      console.warn("[AUTH_HEADER_MISSING]", { url: config.url, method: config.method });
    }
    return config;
  });
  authAxios.interceptors.response.use(
    response => {
      const d = response.data;
      if (d && typeof d === "object" && d.success === true && "data" in d) {
        response.data = d.data;
      }
      return response;
    },
    async error => {
      const d = error.response?.data;
      if (d && d.success === false && d.error) {
        error.response.data.detail = d.error.message;
      }
      const original = error.config;

      // 403 diagnostic: identify missing auth header vs tenant mismatch vs other
      if (error.response?.status === 403) {
        const sentAuth = original?.headers?.Authorization || "";
        const detail = error.response?.data?.detail || "";
        console.warn("[AUTH_403_DETECTED]", {
          url: original?.url,
          auth_present: !!sentAuth,
          auth_first8: sentAuth ? sentAuth.slice(7, 15) : "NONE",
          request_rid: original?._diagRid || "NONE",
          tenant_mismatch: detail.includes("tenant mismatch"),
          detail,
        });
      }

      // 402 → account suspended, redirect to subscription tab
      if (error.response?.status === 402) {
        const msg = error.response?.data?.detail || error.response?.data?.error?.message;
        if (msg && typeof window !== "undefined") {
          // Lazy import to avoid circular deps
          import("sonner").then(({ toast }) => toast.error(msg, { duration: 8000 }));
          if (!window.location.pathname.includes("/settings")) {
            window.location.href = "/settings?tab=subscription";
          }
        }
        return Promise.reject(error);
      }

      // 401 → silent refresh then retry once
      if (error.response?.status === 401 && !original._retry) {
        original._retry = true;
        if (isRefreshing.current) {
          return new Promise((resolve) => {
            refreshQueue.current.push((newToken) => {
              original.headers.Authorization = `Bearer ${newToken}`;
              resolve(authAxios(original));
            });
          });
        }
        const newToken = await silentRefresh();
        if (newToken) {
          original.headers.Authorization = `Bearer ${newToken}`;
          return authAxios(original);
        }
      }

      // 5xx → exponential backoff, max 3 retries (skip auth/upload/AI routes)
      const status = error.response?.status;
      const isRetryable = status >= 500 || !error.response; // also covers network errors
      const notTooManyRetries = (original._retryCount || 0) < 3;
      const notAuthRoute = !original.url?.includes("/auth/");
      const notNoRetry = !original._noRetry; // caller can set _noRetry: true to skip
      if (isRetryable && notTooManyRetries && notAuthRoute && notNoRetry) {
        original._retryCount = (original._retryCount || 0) + 1;
        const delay = Math.min(1000 * Math.pow(2, original._retryCount - 1), 8000);
        await new Promise(r => setTimeout(r, delay));
        return authAxios(original);
      }

      // After retries exhausted (or non-retryable): capture for observability
      // Skip error-logs endpoint itself to avoid infinite loop
      const errUrl = original.url || "";
      if (!errUrl.includes("/error-logs/") && !errUrl.includes("/auth/")) {
        captureError({
          route: errUrl,
          method: original.method || "GET",
          statusCode: status || 0,
          message: error.response?.data?.detail || error.message || "Network error",
          details: { response_status: status, retry_count: original._retryCount || 0 },
        });
      }

      return Promise.reject(error);
    }
  );

  const switchRestaurant = async (restaurantId) => {
    const res = await authAxios.post(`/auth/switch-restaurant/${restaurantId}`);
    saveTokens(res.data.access_token, res.data.refresh_token);
    // Force full page reload to ensure all data is refreshed with new restaurant context
    window.location.href = "/dashboard";
    return res.data;
  };

  return (
    <AuthContext.Provider value={{
      user, token, login, login2fa, register, logout, loading, authAxios,
      authReady: !loading && !!user,
      role, permissions, isOwner, isGerente, isSirviente,
      canSeeFinancials, isSidebarHidden, switchRestaurant, isImpersonating,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
